import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Grid, Paper, Divider, CircularProgress, 
  LinearProgress, Avatar, Stack 
} from "@mui/material";
import { Bar, Pie } from 'react-chartjs-2';
import { 
  Chart as ChartJS, Title, Tooltip, Legend, BarElement, 
  CategoryScale, LinearScale, ArcElement 
} from 'chart.js';
import { 
  Wallet, ShoppingCart, BarChart3, Landmark, 
  PieChart as PieChartIcon, HandCoins 
} from "lucide-react";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement);

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  aqua: "#00C0CA",
  teal: "#008B9C",
  azur: "#28708B",
  dark: "#22404D",
  lime: "#8FC440",
  yellow: "#E3E24F",
  red: "#F65F4E",
  pearl: "#EEF3F4"
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-UY', {
    style: 'currency',
    currency: 'UYU',
    minimumFractionDigits: 0
  }).format(value);
};

const Reporte = ({ idPrograma, actualizar }) => {
  const [reporteData, setReporteData] = useState(null);

  useEffect(() => {
    const fetchReporteData = async () => {
      try {
        const response = await fetch(`${apiUrl}/Programa/HacerReporte?idPrograma=${idPrograma}`, {
          headers: { 'apiKey': localStorage.getItem("apiKey") }
        });
        setReporteData(await response.json());
      } catch (error) { console.error('Error:', error); }
    };
    fetchReporteData();
  }, [idPrograma, actualizar]);

  if (!reporteData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
        <CircularProgress sx={{ color: PALETTE.teal }} />
      </Box>
    );
  }

  const { cantidadAdelantos, montoAdelantos, cantidadGastos, montoGastos, montoRestante, presupuesto } = reporteData;
  const usoPresupuesto = ((presupuesto - montoRestante) / presupuesto) * 100;
  const porcentajeAdelantos = (montoAdelantos / presupuesto) * 100;
  const porcentajeGastos = (montoGastos / presupuesto) * 100;

  return (
    <Box sx={{ p: 1 }}>
      <Grid container spacing={3}>
        
     <Grid item xs={12}>
          <Paper elevation={0} sx={{ 
            p: 3, borderRadius: '24px', bgcolor: PALETTE.dark, color: 'white',
            display: 'flex', justifyContent: 'space-around', alignItems: 'center', flexWrap: 'wrap', gap: 3
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700, letterSpacing: 1 }}>PRESUPUESTO TOTAL</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, color: PALETTE.sky }}>{formatCurrency(presupuesto)}</Typography>
            </Box>
            
            <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.1)', height: 40 }} />
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700, letterSpacing: 1 }}>DINERO EJECUTADO</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, color: PALETTE.red }}>{formatCurrency(montoAdelantos + montoGastos)}</Typography>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.1)', height: 40 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700, letterSpacing: 1 }}>RESTANTE REAL</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, color: PALETTE.lime }}>{formatCurrency(montoRestante)}</Typography>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.1)', height: 40 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700, letterSpacing: 1 }}>USO TOTAL</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>{usoPresupuesto.toFixed(0)}%</Typography>
            </Box>
          </Paper>
        </Grid>
        {/* TARJETA ADELANTOS (Igual a Caja Chica) */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', border: '1px solid #eee', height: '100%' }}>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Avatar sx={{ bgcolor: `${PALETTE.teal}15`, color: PALETTE.teal }}><HandCoins size={20}/></Avatar>
                <Typography variant="caption" sx={{ fontWeight: 800, color: PALETTE.teal, bgcolor: `${PALETTE.teal}10`, px: 1.5, py: 0.5, borderRadius: '8px' }}>
                  {cantidadAdelantos} Pedidos
                </Typography>
             </Box>
             <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>Total Adelantos</Typography>
             <Typography variant="h4" sx={{ fontWeight: 900, color: PALETTE.dark, mb: 1 }}>{formatCurrency(montoAdelantos)}</Typography>
             <LinearProgress 
                variant="determinate" 
                value={porcentajeAdelantos} 
                sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: PALETTE.teal } }} 
             />
             <Typography variant="caption" sx={{ color: '#94A3B8', mt: 1, display: 'block' }}>Representa el {porcentajeAdelantos.toFixed(1)}% del presupuesto</Typography>
          </Paper>
        </Grid>

        {/* TARJETA GASTOS / CAJA CHICA */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', border: '1px solid #eee', height: '100%' }}>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Avatar sx={{ bgcolor: `${PALETTE.red}15`, color: PALETTE.red }}><ShoppingCart size={20}/></Avatar>
                <Typography variant="caption" sx={{ fontWeight: 800, color: PALETTE.red, bgcolor: `${PALETTE.red}10`, px: 1.5, py: 0.5, borderRadius: '8px' }}>
                  {cantidadGastos} Gastos
                </Typography>
             </Box>
             <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>Caja Chica & Consumo</Typography>
             <Typography variant="h4" sx={{ fontWeight: 900, color: PALETTE.dark, mb: 1 }}>{formatCurrency(montoGastos)}</Typography>
             <LinearProgress 
                variant="determinate" 
                value={porcentajeGastos} 
                sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: PALETTE.red } }} 
             />
             <Typography variant="caption" sx={{ color: '#94A3B8', mt: 1, display: 'block' }}>Representa el {porcentajeGastos.toFixed(1)}% del presupuesto</Typography>
          </Paper>
        </Grid>

        {/* TARJETA SALDO DISPONIBLE */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', border: '1px solid #eee', height: '100%' }}>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Avatar sx={{ bgcolor: `${PALETTE.lime}15`, color: PALETTE.lime }}><Wallet size={20}/></Avatar>
                <Typography variant="caption" sx={{ fontWeight: 800, color: PALETTE.lime, bgcolor: `${PALETTE.lime}10`, px: 1.5, py: 0.5, borderRadius: '8px' }}>
                  {(100 - usoPresupuesto).toFixed(1)}% Libre
                </Typography>
             </Box>
             <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>Saldo Remanente</Typography>
             <Typography variant="h4" sx={{ fontWeight: 900, color: PALETTE.dark, mb: 1 }}>{formatCurrency(montoRestante)}</Typography>
             <LinearProgress 
                variant="determinate" 
                value={100 - usoPresupuesto} 
                sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: PALETTE.lime } }} 
             />
             <Typography variant="caption" sx={{ color: '#94A3B8', mt: 1, display: 'block' }}>Fondos aún no ejecutados</Typography>
          </Paper>
        </Grid>

        {/* GRÁFICOS INTERMEDIOS */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: '28px', border: '1px solid #eee', height: '350px' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BarChart3 size={20} color={PALETTE.azur} /> Comparativa de Uso
            </Typography>
            <Box sx={{ height: 250 }}>
              <Bar
                data={{
                  labels: ['Adelantos', 'Gastos'],
                  datasets: [{
                    data: [montoAdelantos, montoGastos],
                    backgroundColor: [PALETTE.teal, PALETTE.red],
                    borderRadius: 12,
                    barThickness: 50,
                  }]
                }}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: '28px', border: '1px solid #eee', height: '350px' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PieChartIcon size={20} color={PALETTE.azur} /> Distribución Total
            </Typography>
            <Box sx={{ height: 240 }}>
              <Pie
                data={{
                  labels: ['Adelantos', 'Gastos', 'Disponible'],
                  datasets: [{
                    data: [montoAdelantos, montoGastos, montoRestante],
                    backgroundColor: [PALETTE.teal, PALETTE.red, PALETTE.lime],
                    borderWidth: 0,
                  }]
                }}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* SUMMARY STRIP GLOBAL (Todo el dinero) */}
        

      </Grid>
    </Box>
  );
};

export default Reporte;